import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// OVH production backend (stocks data source + auth)
// Historical note: Previously called "hetznerApi" in documentation
const API_URL = 'https://api-ovh.alainponcelas.com';
const EXTERNAL_API = `${API_URL}/api`;

// JWT token management
const TOKEN_KEY = 'valoramap_token';

// Diagnostic flag — log scoring fields warning only once per session
let _scoringFieldsWarned = false;

function checkScoringFields(firstStock) {
  if (_scoringFieldsWarned) return;
  if (process.env.NODE_ENV === 'production') return;
  
  const hasAnyScoringField = 
    'altman_zscore' in firstStock ||
    'piotroski_fscore' in firstStock ||
    'beneish_mscore' in firstStock;
  
  if (!hasAnyScoringField) {
    console.info(
      '[VALORAMAP] Scoring fields not yet available in API response. ' +
      'Graceful fallback active.'
    );
    _scoringFieldsWarned = true;
  }
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Create axios instance (Emergent backend — markets, watchlists, payments, etc.)
const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

// External API axios instance (stocks, auth, and all primary data)
const externalApi = axios.create({
  baseURL: EXTERNAL_API,
  headers: { 'Content-Type': 'application/json' }
});

// Add JWT token to every request (both instances)
function addAuthInterceptor(instance) {
  instance.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  
  // Handle 401/403 responses
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Skip auto-redirect for screener page (supports preview mode)
      const isScreenerPage = window.location.pathname === '/screener';
      // Skip redirect for stocks/screen API calls (let ScreenerPage handle fallback)
      const isScreenerApiCall = error.config?.url?.includes('/stocks/screen');
      
      if (error.response?.status === 401) {
        // 401 Unauthorized - invalid/expired token
        removeToken();
        // Don't redirect if on screener (preview mode) or login page
        if (!isScreenerPage && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else if (error.response?.status === 403) {
        // 403 Forbidden - no active subscription
        // Don't redirect if on screener or if it's a screener API call (let component handle fallback)
        if (!isScreenerPage && !isScreenerApiCall && window.location.pathname !== '/pricing') {
          window.location.href = '/pricing?reason=no_subscription';
        }
        // Return a specific error that components can identify
        error.isAccessDenied = true;
        error.message = 'Abonnement requis';
      }
      return Promise.reject(error);
    }
  );
}

addAuthInterceptor(api);
addAuthInterceptor(externalApi);

// Map frontend sort keys to API field names
const SORT_KEY_MAP = {
  symbol: 'symbol',
  price: 'market_cap',
  change: 'market_cap',
  pe: 'pe_ratio',
  pb: 'pb_ratio',
  roe: 'roe',
  marketCap: 'market_cap',
  valuation_score: 'pe_ratio',
  liquidity_score: 'liquidity_score',
  valoramap_score: 'valoramap_score',
};

// Map API stock response fields to frontend format
function mapStock(s) {
  return {
    symbol: s.symbol,
    name: s.name,
    market: s.market,
    country: s.country,
    sector: s.sector,
    pe: s.pe_ratio,
    pb: s.pb_ratio,
    roe: s.roe,
    real_roe: s.real_roe ?? null,
    marketCap: s.market_cap,
    price: s.price,
    change: s.change ?? null,
    volume: s.volume ?? null,
    eps: s.eps ?? null,
    currency: s.currency,
    dividend_yield: s.dividend_yield,
    valoramap_score: s.valoramap_score ?? null,
    valoramap_score_basis: s.valoramap_score_basis ?? null,
    liquidity_score: s.liquidity_score ?? null,
    liquidity_risk: s.liquidity_risk ?? null,
    has_adr: s.has_adr ?? false,
    adr_ticker: s.adr_ticker ?? null,
    next_earnings: s.next_earnings ?? null,
    days_to_earnings: s.days_to_earnings ?? null,
    // Scoring fields — preserve undefined vs null distinction for graceful degradation
    altman_zscore: 'altman_zscore' in s ? s.altman_zscore : undefined,
    altman_zone: 'altman_zone' in s ? s.altman_zone : undefined,
    piotroski_fscore: 'piotroski_fscore' in s ? s.piotroski_fscore : undefined,
    piotroski_coverage: 'piotroski_coverage' in s ? s.piotroski_coverage : undefined,
    piotroski_zone: 'piotroski_zone' in s ? s.piotroski_zone : undefined,
    beneish_mscore: 'beneish_mscore' in s ? s.beneish_mscore : undefined,
    beneish_coverage: 'beneish_coverage' in s ? s.beneish_coverage : undefined,
    beneish_zone: 'beneish_zone' in s ? s.beneish_zone : undefined,
    scoring_exclusion_reason: 'scoring_exclusion_reason' in s ? s.scoring_exclusion_reason : undefined,
  };
}

// =====================
// AUTH API
// =====================

export const authAPI = {
  // Register with email/password
  register: async (email, password, name, subscribeNewsletter = false) => {
    const response = await externalApi.post('/auth/register', {
      email, password, name, subscribe_newsletter: subscribeNewsletter
    });
    const { token, user } = response.data;
    if (token) setToken(token);
    return user;
  },

  // Login with email/password
  login: async (email, password) => {
    const response = await externalApi.post('/auth/login', { email, password });
    const { token, user } = response.data;
    if (token) setToken(token);
    return user;
  },

  // Exchange temporary OAuth code for JWT
  exchange: async (code) => {
    const response = await externalApi.post('/auth/exchange', { code });
    const { token, user } = response.data;
    if (token) setToken(token);
    return user;
  },

  // Get current user
  getMe: async () => {
    const response = await externalApi.get('/auth/me');
    return response.data;
  },

  // Logout (client-side only — just remove the token)
  logout: () => {
    removeToken();
  },

  // Forgot password
  forgotPassword: async (email) => {
    const response = await externalApi.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password
  resetPassword: async (token, password) => {
    const response = await externalApi.post('/auth/reset-password', { token, password });
    return response.data;
  }
};

// =====================
// STOCKS API
// =====================

export const stocksAPI = {
  getSectors: async (country) => {
    const params = {};
    if (country) params.country = country;
    // Backend endpoint: /api/public/stocks/sectors (no auth required for sectors list)
    const response = await externalApi.get('/public/stocks/sectors', { params });
    return response.data;
  },

  getStocks: async (params = {}) => {
    const apiParams = {};
    if (params.page) apiParams.page = params.page;
    if (params.limit) apiParams.limit = params.limit;
    if (params.sort) apiParams.sort = SORT_KEY_MAP[params.sort] || params.sort;
    if (params.order) apiParams.order = params.order;
    if (params.market) apiParams.country = params.market;
    if (params.sector) apiParams.sector = params.sector;
    if (params.search) apiParams.search = params.search;
    if (params.pe_min) apiParams.pe_min = params.pe_min;
    if (params.pe_max) apiParams.pe_max = params.pe_max;
    if (params.pb_min) apiParams.pb_min = params.pb_min;
    if (params.pb_max) apiParams.pb_max = params.pb_max;
    if (params.roe_min) apiParams.roe_min = params.roe_min;
    if (params.roe_max) apiParams.roe_max = params.roe_max;
    if (params.liquidity_min) apiParams.liquidity_min = params.liquidity_min;
    if (params.piotroski_min) apiParams.piotroski_min = params.piotroski_min;
    if (params.altman_min) apiParams.altman_min = params.altman_min;
    if (params.dividend_yield_min) apiParams.dividend_yield_min = params.dividend_yield_min;
    if (params.dividend_yield_max) apiParams.dividend_yield_max = params.dividend_yield_max;
    if (params.market_cap_min) apiParams.market_cap_min = params.market_cap_min;
    if (params.market_cap_max) apiParams.market_cap_max = params.market_cap_max;
    if (params.catalyst_ftse_eligible) apiParams.catalyst_ftse_eligible = params.catalyst_ftse_eligible;

    // Backend endpoint: /api/public/stocks/screen (public access)
    const response = await externalApi.get('/public/stocks/screen', { params: apiParams });
    const data = response.data;
    const limit = params.limit || 15;
    
    // Check for scoring fields availability (dev diagnostic, once per session)
    const rawStocks = data.stocks || [];
    if (rawStocks.length > 0) {
      checkScoringFields(rawStocks[0]);
    }
    
    return {
      stocks: rawStocks.map(mapStock),
      total: data.total_matching || data.count || 0,
      pages: Math.ceil((data.total_matching || data.count || 0) / limit),
    };
  },

  getStock: async (market, symbol) => {
    // Backend endpoint: /api/stocks/{market}/{symbol} (requires auth)
    const response = await externalApi.get(`/stocks/${market}/${symbol}`);
    return response.data;
  },

  getStockHistory: async (market, symbol, period = '1Y') => {
    const response = await externalApi.get(`/stocks/${market}/${symbol}/history`, { params: { period } });
    return response.data;
  },

  getMarketAccess: async (ticker, country = 'CH') => {
    const response = await externalApi.get(`/public/stock/${ticker}/access`, { params: { country } });
    return response.data;
  },

  getAccountingQuality: async (symbol) => {
    const response = await externalApi.get(`/analysis/accounting-quality/${symbol}`);
    return response.data;
  }
};

// =====================
// MARKETS API
// =====================

export const marketsAPI = {
  getMarkets: async () => {
    const response = await externalApi.get('/markets');
    return response.data;
  },
  getMarket: async (code) => {
    const response = await externalApi.get(`/markets/${code}`);
    return response.data;
  }
};

// =====================
// WATCHLISTS API
// =====================

export const watchlistsAPI = {
  getWatchlists: async () => {
    const response = await externalApi.get('/watchlists');
    return response.data;
  },
  createWatchlist: async (data) => {
    const response = await externalApi.post('/watchlists', data);
    return response.data;
  },
  getWatchlist: async (id) => {
    const response = await externalApi.get(`/watchlists/${id}`);
    return response.data;
  },
  updateWatchlist: async (id, data) => {
    const response = await externalApi.put(`/watchlists/${id}`, data);
    return response.data;
  },
  deleteWatchlist: async (id) => {
    const response = await externalApi.delete(`/watchlists/${id}`);
    return response.data;
  },
  addStock: async (watchlistId, market, symbol) => {
    const response = await externalApi.post(`/watchlists/${watchlistId}/stocks`, { market, symbol });
    return response.data;
  },
  removeStock: async (watchlistId, market, symbol) => {
    const response = await externalApi.delete(`/watchlists/${watchlistId}/stocks/${market}/${symbol}`);
    return response.data;
  }
};

// =====================
// PAYMENTS API (Emergent backend — NOT external)
// =====================

export const paymentsAPI = {
  createCheckout: async (plan) => {
    const response = await api.post('/payments/create-checkout', { plan, origin_url: window.location.origin });
    return response.data;
  },
  getPaymentStatus: async (sessionId) => {
    const response = await api.get(`/payments/status/${sessionId}`);
    return response.data;
  },
  getSubscription: async () => {
    const response = await api.get('/payments/subscription');
    return response.data;
  }
};

// =====================
// MACRO TRENDS API
// =====================

export const macroAPI = {
  getAllTrends: async () => {
    const response = await externalApi.get('/public/macro/trends');
    return response.data;
  },
  getTrends: async (countryCode) => {
    const response = await externalApi.get(`/public/macro/trends/${countryCode}`);
    return response.data;
  },
  refreshTrends: async () => {
    const response = await externalApi.post('/public/macro/trends/refresh');
    return response.data;
  }
};

// =====================
// LIQUIDITY API
// =====================

export const liquidityAPI = {
  getLiquidity: async (market, symbol) => {
    const response = await externalApi.get(`/liquidity/${market}/${symbol}`);
    return response.data;
  },
  getStatus: async () => {
    const response = await externalApi.get('/liquidity/status');
    return response.data;
  },
  refresh: async (market, limit = 50) => {
    const response = await externalApi.post(`/liquidity/refresh/${market}`, null, { params: { limit } });
    return response.data;
  }
};

// =====================
// SANCTIONS API
// =====================

export const sanctionsAPI = {
  getSanctions: async (market, symbol) => {
    const response = await externalApi.get(`/sanctions/${market}/${symbol}`);
    return response.data;
  },
  getCountryRisk: async (market) => {
    const response = await externalApi.get(`/sanctions/country/${market}`);
    return response.data;
  },
  getStatus: async () => {
    const response = await externalApi.get('/sanctions/status');
    return response.data;
  },
  getRiskLevels: async () => {
    const response = await externalApi.get('/sanctions/risk-levels');
    return response.data;
  },
  refresh: async (market, limit = 50) => {
    const response = await externalApi.post(`/sanctions/refresh/${market}`, null, { params: { limit } });
    return response.data;
  }
};

// =====================
// ONBOARDING API
// =====================

export const onboardingAPI = {
  getStatus: async () => {
    const response = await externalApi.get('/auth/onboarding/status');
    return response.data;
  },
  complete: async (preferredMarket = null) => {
    const response = await externalApi.post('/auth/onboarding/complete', { preferred_market: preferredMarket });
    return response.data;
  }
};

// =====================
// INTELLIGENCE REPORTS API
// =====================

export const intelligenceAPI = {
  getReports: async (params = {}) => {
    const response = await externalApi.get('/intelligence/reports', { params });
    return response.data;
  },
  getReport: async (market, symbol) => {
    const response = await externalApi.get(`/intelligence/reports/${market}/${symbol}`);
    return response.data;
  },
  downloadPDF: async (market, symbol) => {
    const response = await externalApi.get(`/intelligence/reports/${market}/${symbol}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// =====================
// ANALYSIS API (Phase 2)
// =====================

export const analysisAPI = {
  getInflation: async (countryCode) => {
    const response = await externalApi.get(`/analysis/inflation/${countryCode}`);
    return response.data;
  },
  getRealMetrics: async (market, symbol) => {
    const response = await externalApi.get(`/analysis/real-metrics/${market}/${symbol}`);
    return response.data;
  },
  compareStocks: async (symbols) => {
    const response = await externalApi.get('/analysis/real-metrics/compare', { params: { symbols } });
    return response.data;
  },
  getAccountingQuality: async (market, symbol) => {
    const response = await externalApi.get(`/analysis/accounting-quality/${market}/${symbol}`);
    return response.data;
  },
  getBeneishScore: async (market, symbol) => {
    const response = await externalApi.get(`/analysis/beneish/${market}/${symbol}`);
    return response.data;
  },
  getAltmanScore: async (market, symbol) => {
    const response = await externalApi.get(`/analysis/altman/${market}/${symbol}`);
    return response.data;
  },
  getPiotroskiScore: async (market, symbol) => {
    const response = await externalApi.get(`/analysis/piotroski/${market}/${symbol}`);
    return response.data;
  },
  getCashFlowQuality: async (market, symbol) => {
    const response = await externalApi.get(`/analysis/cash-flow-quality/${market}/${symbol}`);
    return response.data;
  }
};

export default api;
